import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon, ZapIcon, ShieldCheckIcon, TargetIcon, MapPinIcon, StarIcon, GlobeIcon, TrendingUpIcon, DollarSignIcon, PhoneIcon, MailIcon, CpuIcon, BarChart3Icon, VideoIcon } from 'lucide-react';
import { ProofCarousel } from './ProofCarousel';
import emailjs from '@emailjs/browser';
// ⚠️ CONFIGURACIÓN EMAILJS — Reemplaza estos valores con los tuyos:
// 1. Crea cuenta gratis en https://www.emailjs.com
// 2. Agrega un Email Service (Gmail) → copia el Service ID
// 3. Crea un Email Template con variables: {{nombre}}, {{negocio}}, {{telefono}}, {{inversion}}, {{mensaje}}
// 4. Copia tu Public Key desde Account → API Keys
const EMAILJS_SERVICE_ID = 'service_5st6tfj';
const EMAILJS_TEMPLATE_ADMIN = 'template_kwi4ynr';
const EMAILJS_TEMPLATE_AUTOREPLY = 'template_2deikke';
const EMAILJS_PUBLIC_KEY = 'vWvHy5XmFe6E1h_h6';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6TdzGPbST4y3Gk6CzyrcQT/IMG_6635.jpg';
const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.15,
      ease: 'easeIn'
    }
  }
};
const mobileMenuVariants = {
  hidden: {
    opacity: 0,
    x: '100%'
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    x: '100%',
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  }
};
const accordionVariants = {
  hidden: {
    opacity: 0,
    height: 0
  },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.2
    }
  }
};
export function NavigationHeader() {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);
  const toggleDropdown = (section: string) => {
    setActiveDropdown(activeDropdown === section ? null : section);
  };
  const toggleMobileSection = (section: string) => {
    setMobileExpandedSection(mobileExpandedSection === section ? null : section);
  };
  const closeAll = () => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };
  const navItems = [{
    id: 'agencia',
    label: 'LA AGENCIA'
  }, {
    id: 'sistema',
    label: 'SISTEMA DE ADQUISICIÓN'
  }, {
    id: 'casos',
    label: 'CASOS DE ÉXITO'
  }, {
    id: 'contacto',
    label: 'CONTACTO / SOPORTE'
  }];
  return <div ref={headerRef} className="fixed top-0 left-0 right-0 z-50" data-id="element-13">
      {/* Main Header Bar */}
      <div className="bg-[#000000] border-b border-[#D4A843]/20 relative z-50" data-id="element-14">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-15">
          {/* Logo */}
          <a href="#" onClick={closeAll} className="flex-shrink-0" data-id="element-16">
            <img src={LOGO_URL} alt="Doctor Ecommerce Logo" className="h-20 md:h-24" data-id="element-17" />
          </a>

          {/* Desktop Nav Links */}
          <nav className="hidden lg:flex items-center gap-8" data-id="element-18">
            {navItems.map(item => <button key={item.id} onClick={() => toggleDropdown(item.id)} className={`text-sm font-semibold tracking-wide transition-colors flex items-center gap-1 ${activeDropdown === item.id ? 'gold-text' : 'text-gray-300 hover:text-white'}`} data-id="element-19">
                {item.label}
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === item.id ? 'rotate-180' : ''}`} data-id="element-20" />
              </button>)}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:block" data-id="element-21">
            <a href="#formulario" onClick={closeAll} className="inline-block gold-gradient text-black font-bold px-6 py-2.5 rounded-lg text-sm gold-glow gold-glow-hover transition-all duration-300" data-id="element-22">
              RESERVAR DIAGNÓSTICO GRATIS
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-23">
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-24" /> : <MenuIcon className="w-6 h-6" data-id="element-25" />}
          </button>
        </div>
      </div>

      {/* Desktop Dropdown Panels */}
      <div className="hidden lg:block relative" data-id="element-26">
        <AnimatePresence data-id="element-27">
          {activeDropdown && <motion.div initial="hidden" animate="visible" exit="exit" variants={dropdownVariants} className="absolute top-0 left-0 right-0 bg-[#0A0A0A] border-b border-[#D4A843]/30 shadow-2xl shadow-black/50 overflow-hidden" data-id="element-28">
              <div className="max-w-6xl mx-auto px-8 py-12" data-id="element-29">
                {activeDropdown === 'agencia' && <AgenciaContent data-id="element-30" />}
                {activeDropdown === 'sistema' && <SistemaContent data-id="element-31" />}
                {activeDropdown === 'casos' && <CasosContent closeAll={closeAll} data-id="element-32" />}
                {activeDropdown === 'contacto' && <ContactoContent data-id="element-33" />}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Mobile Full-Screen Menu */}
      <AnimatePresence data-id="element-34">
        {isMobileMenuOpen && <motion.div initial="hidden" animate="visible" exit="exit" variants={mobileMenuVariants} className="fixed inset-0 z-40 bg-[#0A0A0A] overflow-y-auto pt-32 pb-24 px-4 lg:hidden" data-id="element-35">
            <div className="flex flex-col gap-2" data-id="element-36">
              {navItems.map(item => <div key={item.id} className="border-b border-gray-800" data-id="element-37">
                  <button onClick={() => toggleMobileSection(item.id)} className="w-full flex items-center justify-between py-4 text-left font-bold text-white" data-id="element-38">
                    <span className={mobileExpandedSection === item.id ? 'gold-text' : ''} data-id="element-39">
                      {item.label}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${mobileExpandedSection === item.id ? 'rotate-180 text-[#D4A843]' : 'text-gray-500'}`} data-id="element-40" />
                  </button>
                  <AnimatePresence data-id="element-41">
                    {mobileExpandedSection === item.id && <motion.div initial="hidden" animate="visible" exit="exit" variants={accordionVariants} className="overflow-hidden" data-id="element-42">
                        <div className="pb-6 pt-2" data-id="element-43">
                          {item.id === 'agencia' && <AgenciaContent isMobile data-id="element-44" />}
                          {item.id === 'sistema' && <SistemaContent isMobile data-id="element-45" />}
                          {item.id === 'casos' && <CasosContent isMobile closeAll={closeAll} data-id="element-46" />}
                          {item.id === 'contacto' && <ContactoContent isMobile data-id="element-47" />}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>

            <div className="mt-8" data-id="element-48">
              <a href="#formulario" onClick={closeAll} className="block w-full text-center gold-gradient text-black font-bold px-6 py-4 rounded-lg text-base gold-glow transition-all duration-300" data-id="element-49">
                RESERVAR DIAGNÓSTICO GRATIS
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Dropdown Content Components ---
function AgenciaContent({
  isMobile = false
}: {
  isMobile?: boolean;
}) {
  return <div className="max-w-3xl mx-auto text-center" data-id="element-50">
      <h2 className={`gold-text font-bold mb-6 ${isMobile ? 'text-2xl' : 'text-4xl'}`} data-id="element-51">
        No somos creativos. Somos especialistas en tu facturación.
      </h2>
      <div className="border-l-4 border-[#D4A843] pl-6 text-left" data-id="element-52">
        <p className="text-gray-300 mb-4 text-base md:text-lg leading-relaxed" data-id="element-53">
          Mientras otras agencias se enfocan en que tu Instagram se vea 'bonito'
          o en entregarte reportes llenos de tecnicismos que no entiendes, en
          Doctor Ecommerce tenemos una sola obsesión:{' '}
          <span className="text-white font-semibold" data-id="element-54">
            Que tu teléfono no pare de sonar.
          </span>
        </p>
        <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-55">
          Nacimos en el corazón de Tampa para rescatar a los dueños de negocios
          latinos que han sido engañados por 'gurús' y agencias que solo
          apuestan con tu dinero. Entendemos tu sacrificio, hablamos tu idioma y
          aplicamos ingeniería de precisión para que cada dólar que inviertas
          regrese multiplicado en clientes reales. No somos tu agencia, somos el
          departamento de crecimiento que tu negocio necesitaba para dominar el
          mercado de Estados Unidos.
        </p>
      </div>
    </div>;
}
function SistemaContent({
  isMobile = false
}: {
  isMobile?: boolean;
}) {
  const baseServices = ['Gestión de Meta Ads (Facebook / Instagram)', 'Google Ads', 'TikTok Ads', 'YouTube Ads', 'Email Marketing Automatizado', 'SMS Marketing', 'Retargeting / Remarketing', 'Diseño y optimización de Landing Pages', 'Diseño Web Profesional', 'CRO (Optimización de Conversión)', 'SEO Estratégico', 'Google Business Profile (Maps)', 'Branding Premium', 'Gestión de Redes Sociales'];
  const advancedServices = [{
    label: 'Automatización con IA para Ventas',
    sub: 'Chatbots inteligentes · WhatsApp · Seguimiento automatizado'
  }, {
    label: 'Sistemas Omnicanal',
    sub: 'Ads + Email + SMS + Retargeting con flujos automáticos'
  }, {
    label: 'Funnels Quirúrgicos',
    sub: 'Doctores · Realtors · eCommerce · Clínicas'
  }, {
    label: 'Implementación CRM',
    sub: 'GoHighLevel · HubSpot · ActiveCampaign'
  }, {
    label: 'Data & Tracking Avanzado',
    sub: 'Server-side tracking · Eventos personalizados · Análisis de comportamiento'
  }, {
    label: 'Video Marketing IA',
    sub: 'Creativos UGC estratégicos · Pruebas A/B automáticas'
  }, {
    label: 'Web Dinámica',
    sub: 'Contenido y ofertas que cambian según ubicación del tráfico'
  }, {
    label: 'AI Search Optimization (ASO)',
    sub: 'Optimización para ChatGPT y AI Overviews'
  }, {
    label: 'Blindaje Digital & Protección Anti-Ban',
    sub: 'Cuentas de agencia de alta confianza · Estructuras resilientes'
  }, {
    label: 'PR Digital & Posicionamiento en Medios',
    sub: 'Autoridad de marca · Publicaciones en medios de alto perfil'
  }, {
    label: 'Gestión de Reputación Automatizada',
    sub: 'Sistema de reseñas · Control de narrativa online'
  }];
  return <div data-id="element-56">
      <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-2'}`} data-id="element-57">
        {/* ═══ BLOQUE 1: SERVICIOS BASE ═══ */}
        <div className="bg-[#0A0A0A] border border-gray-800/60 rounded-2xl p-6 md:p-8" data-id="element-58">
          <div className="flex items-center gap-3 mb-6" data-id="element-59">
            <div className="w-8 h-8 rounded-lg bg-[#D4A843]/10 flex items-center justify-center" data-id="element-60">
              <ZapIcon className="w-4 h-4 text-[#D4A843]" data-id="element-61" />
            </div>
            <h3 className="text-white font-bold text-lg" data-id="element-62">
              Ecosistema de Adquisición Base
            </h3>
          </div>
          <div className="h-px bg-gray-800/60 mb-5" data-id="element-63" />
          <ul className="space-y-3" data-id="element-64">
            {baseServices.map((service, idx) => <li key={idx} className="flex items-center gap-3" data-id="element-65">
                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#D4A843]" data-id="element-66" />
                <span className="text-gray-300 text-sm" data-id="element-67">{service}</span>
              </li>)}
          </ul>
        </div>

        {/* ═══ BLOQUE 2: SERVICIOS AVANZADOS 2025–2026 ═══ */}
        <div className="relative rounded-2xl overflow-hidden" data-id="element-68">
          {/* Gold glow border */}
          <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-[#D4A843]/50 via-[#D4A843]/20 to-[#D4A843]/5" data-id="element-69" />
          {/* Ambient glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[40%] bg-[#D4A843] opacity-[0.04] blur-[60px] rounded-full pointer-events-none" data-id="element-70" />

          <div className="relative bg-[#0A0A0A] rounded-2xl p-6 md:p-8" data-id="element-71">
            <div className="flex items-center gap-3 mb-2" data-id="element-72">
              <div className="w-8 h-8 rounded-lg bg-[#D4A843]/15 border border-[#D4A843]/30 flex items-center justify-center" data-id="element-73">
                <CpuIcon className="w-4 h-4 text-[#D4A843]" data-id="element-74" />
              </div>
              <h3 className="text-white font-bold text-lg" data-id="element-75">
                Arquitectura Avanzada
              </h3>
            </div>
            <p className="text-[#D4A843]/50 text-xs font-semibold tracking-[0.2em] uppercase mb-5 ml-11" data-id="element-76">
              Innovación 2025–2026
            </p>
            <div className="h-px bg-gradient-to-r from-[#D4A843]/30 via-[#D4A843]/10 to-transparent mb-5" data-id="element-77" />

            <ul className="space-y-4" data-id="element-78">
              {advancedServices.map((service, idx) => <li key={idx} className="group flex items-start gap-3" data-id="element-79">
                  <div className="flex-shrink-0 w-5 h-5 rounded bg-[#D4A843]/10 flex items-center justify-center mt-0.5 group-hover:bg-[#D4A843]/20 transition-colors" data-id="element-80">
                    <ZapIcon className="w-3 h-3 text-[#D4A843]" data-id="element-81" />
                  </div>
                  <div data-id="element-82">
                    <span className="text-white text-sm font-semibold block leading-tight" data-id="element-83">
                      {service.label}
                    </span>
                    <span className="text-gray-500 text-xs leading-tight" data-id="element-84">
                      {service.sub}
                    </span>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}
function CasosContent({
  isMobile = false,
  closeAll
}: {
  isMobile?: boolean;
  closeAll: () => void;
}) {
  return <div data-id="element-85">
      <h2 className={`gold-text font-bold mb-8 text-center ${isMobile ? 'text-2xl' : 'text-3xl'}`} data-id="element-86">
        Historias Reales de Negocios que Dejaron de Sobrevivir para Empezar a
        Dominar.
      </h2>

      {/* Auto-scrolling proof carousel */}
      <div className="mb-10 -mx-8" data-id="element-87">
        <ProofCarousel compact data-id="element-88" />
      </div>

      <div className={`grid gap-6 mb-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`} data-id="element-89">
        {/* Case A */}
        <div className="bg-[#1A1A1A] border border-[#D4A843]/30 rounded-xl p-6 relative overflow-hidden group hover:border-[#D4A843]/60 transition-colors" data-id="element-90">
          <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-91">
            <TrendingUpIcon className="w-24 h-24 text-[#D4A843]" data-id="element-92" />
          </div>
          <div className="relative z-10" data-id="element-93">
            <span className="gold-text text-xs font-bold uppercase tracking-wider mb-2 block" data-id="element-94">
              Roofing — Texas
            </span>
            <h3 className="text-xl text-white font-bold mb-4" data-id="element-95">
              El Renacer de un Roofing en Texas
            </h3>
            <p className="text-gray-300 italic text-sm leading-relaxed" data-id="element-96">
              "Pasaron de 2 llamadas semanales a tener la agenda llena con un
              mes de anticipación. Implementamos el Sistema A.C.E. y logramos un
              ROAS de 12x en los primeros 15 días."
            </p>
          </div>
        </div>

        {/* Case B */}
        <div className="bg-[#1A1A1A] border border-[#D4A843]/30 rounded-xl p-6 relative overflow-hidden group hover:border-[#D4A843]/60 transition-colors" data-id="element-97">
          <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-98">
            <DollarSignIcon className="w-24 h-24 text-[#D4A843]" data-id="element-99" />
          </div>
          <div className="relative z-10" data-id="element-100">
            <span className="gold-text text-xs font-bold uppercase tracking-wider mb-2 block" data-id="element-101">
              Spa — Miami
            </span>
            <h3 className="text-xl text-white font-bold mb-4" data-id="element-102">
              Dominio Total en un Spa de Miami
            </h3>
            <p className="text-gray-300 italic text-sm leading-relaxed" data-id="element-103">
              "La dueña estaba a punto de cerrar por falta de clientes.
              Rediseñamos su oferta irresistible y hoy factura más de $40k
              adicionales al mes."
            </p>
          </div>
        </div>
      </div>

      <div className="text-center mb-8" data-id="element-104">
        <h4 className="text-white font-bold mb-2" data-id="element-105">Reseñas Vivas</h4>
        <p className="text-gray-500 italic text-sm max-w-2xl mx-auto" data-id="element-106">
          Capturas de pantalla reales de WhatsApp de dueños de negocios
          agradecidos y fotos de sus equipos de trabajo en plena acción.
        </p>
      </div>

      <div className="text-center" data-id="element-107">
        <a href="#casos" onClick={closeAll} className="inline-block border border-[#D4A843] text-[#D4A843] hover:bg-[#D4A843] hover:text-black font-bold px-6 py-2 rounded-lg text-sm transition-colors" data-id="element-108">
          VER TODOS LOS CASOS
        </a>
      </div>
    </div>;
}
function ContactoContent({
  isMobile = false
}: {
  isMobile?: boolean;
}) {
  const [formData, setFormData] = useState({
    nombre: '',
    negocio: '',
    telefono: '',
    inversion: '',
    mensaje: ''
  });
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendStatus('sending');
    try {
      const templateParams = {
        nombre: formData.nombre,
        email: '',
        negocio: formData.negocio || 'No especificado',
        telefono: formData.telefono,
        inversion: formData.inversion || 'No especificado',
        mensaje: formData.mensaje || 'Sin mensaje adicional',
        to_email: 'doctor.ecommerceus@gmail.com',
        page_url: window.location.href,
        date: new Date().toLocaleString()
      };
      // 1) Email para ADMIN
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ADMIN, templateParams, EMAILJS_PUBLIC_KEY);
      // 2) Email AUTO-REPLY (solo si hay email del cliente)
      if (templateParams.email) {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_AUTOREPLY, templateParams, EMAILJS_PUBLIC_KEY);
      }
      setSendStatus('sent');
      setFormData({
        nombre: '',
        negocio: '',
        telefono: '',
        inversion: '',
        mensaje: ''
      });
      // Reset status after 4 seconds
      setTimeout(() => setSendStatus('idle'), 4000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSendStatus('error');
      setTimeout(() => setSendStatus('idle'), 4000);
    }
  };
  return <div data-id="element-109">
      <h2 className={`gold-text font-bold mb-8 text-center ${isMobile ? 'text-2xl' : 'text-3xl'}`} data-id="element-110">
        Estás a un paso de la cura para tu negocio.
      </h2>

      <div className={`grid gap-10 ${isMobile ? 'grid-cols-1' : 'grid-cols-5'}`} data-id="element-111">
        {/* Form Column */}
        <div className={isMobile ? '' : 'col-span-3'} data-id="element-112">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-113">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-114">
              <div data-id="element-115">
                <input type="text" name="nombre" required value={formData.nombre} onChange={handleChange} placeholder="Nombre Completo *" className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-116" />
              </div>
              <div data-id="element-117">
                <input type="text" name="negocio" value={formData.negocio} onChange={handleChange} placeholder="Nombre del Negocio" className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-118" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-119">
              <div data-id="element-120">
                <input type="tel" name="telefono" required value={formData.telefono} onChange={handleChange} placeholder="Teléfono (WhatsApp) *" className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-121" />
              </div>
              <div data-id="element-122">
                <select name="inversion" value={formData.inversion} onChange={handleChange} className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-123">
                  <option value="" data-id="element-124">
                    ¿Cuánto estás invirtiendo hoy en publicidad?
                  </option>
                  <option value="0-500" data-id="element-125">$0 - $500</option>
                  <option value="500-2000" data-id="element-126">$500 - $2,000</option>
                  <option value="2000+" data-id="element-127">$2,000+</option>
                </select>
              </div>
            </div>

            <div data-id="element-128">
              <textarea name="mensaje" value={formData.mensaje} onChange={handleChange} rows={3} placeholder="Escribe aquí tu mayor problema..." className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm resize-none" data-id="element-129" />
            </div>

            <button type="submit" disabled={sendStatus === 'sending'} className={`w-full font-bold py-3 rounded-lg text-sm transition-all duration-300 ${sendStatus === 'sent' ? 'bg-green-500 text-white' : sendStatus === 'error' ? 'bg-red-500 text-white' : sendStatus === 'sending' ? 'gold-gradient text-black opacity-70 cursor-wait' : 'gold-gradient text-black gold-glow hover:scale-[1.02]'}`} data-id="element-130">
              {sendStatus === 'sending' && 'ENVIANDO...'}
              {sendStatus === 'sent' && '✓ ¡MENSAJE ENVIADO!'}
              {sendStatus === 'error' && '✕ ERROR — INTENTA DE NUEVO'}
              {sendStatus === 'idle' && 'ENVIAR MENSAJE'}
            </button>

            <p className="text-center text-gray-500 text-xs mt-2 flex items-center justify-center gap-1.5" data-id="element-131">
              <ZapIcon className="w-3.5 h-3.5 text-[#D4A843]" data-id="element-132" />
              Respondemos en menos de 2 horas
            </p>
          </form>
        </div>

        {/* Info Column */}
        <div className={`flex flex-col justify-center space-y-6 ${isMobile ? 'border-t border-gray-800 pt-8 mt-4' : 'col-span-2 pl-8 border-l border-gray-800'}`} data-id="element-133">
          <div className="flex items-center gap-4" data-id="element-134">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 border border-[#D4A843]/30" data-id="element-135">
              <MapPinIcon className="w-5 h-5 text-[#D4A843]" data-id="element-136" />
            </div>
            <span className="text-gray-300 text-sm" data-id="element-137">
              1001 WATER ST, TAMPA FL 33602
            </span>
          </div>

          <div className="flex items-center gap-4" data-id="element-138">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 border border-[#D4A843]/30" data-id="element-139">
              <PhoneIcon className="w-5 h-5 text-[#D4A843]" data-id="element-140" />
            </div>
            <span className="text-gray-300 text-sm" data-id="element-141">402-872-0639</span>
          </div>

          <div className="flex items-center gap-4" data-id="element-142">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 border border-[#D4A843]/30" data-id="element-143">
              <MailIcon className="w-5 h-5 text-[#D4A843]" data-id="element-144" />
            </div>
            <span className="text-gray-300 text-sm" data-id="element-145">
              doctor.ecommerceus@gmail.com
            </span>
          </div>
        </div>
      </div>
    </div>;
}